import React from 'react';
import { FunctionField } from 'react-admin';
import { buildCustomExporter } from 'resources/rate_settings/buildCustomExporter';
import { calcPayoutRate, calcTotalPay } from 'resources/rate_settings/rateSettingsCalculations';
import { RateSettingsDisplayHelper } from 'utils/displayHelpers/rate-settings';
import { DisplayHelpers } from 'utils/helpers';

export const getExporter = (filenamePrefix, isMarket, settingsNestedOperator) => (rateSettings) => {
  if (!rateSettings || rateSettings.length === 0) {
    return true;
  }

  return buildCustomExporter(rateSettings, filenamePrefix, isMarket, settingsNestedOperator);
};

export const getRateSettingsInfo = (settings, settingsNestedOperator?, location?) => {
  const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({ settings, location });
  return {
    chargeRateCents: rateSettingsDisplayHelper.getChargeRateCents(settingsNestedOperator),
    rateSettingsPrefix: rateSettingsDisplayHelper.getActiveRateObjKey(settingsNestedOperator),
    activeStructureLabel:
      rateSettingsDisplayHelper.getActiveRateStructureLabel(settingsNestedOperator),
  };
};

export const getRateSettingsHash = (settings, settingsNestedOperator) => {
  const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({ settings });
  const rateSettingHash =
    rateSettingsDisplayHelper.getSettingsForClassification(settingsNestedOperator);

  return rateSettingHash;
};

const createCalcTotalPayParams = (
  settings,
  settingsNestedOperator,
  settingsType = 'non_blended_settings',
) => {
  const rateSettingHash = getRateSettingsHash(settings, settingsNestedOperator);
  const settingsObj = rateSettingHash?.[settingsType];

  return {
    charge_max_daily_regular_hours: settingsObj?.charge_max_daily_regular_hours,
    charge_max_daily_ot_hours: settingsObj?.charge_max_daily_overtime_hours,
    charge_overtime_multiplier: settingsObj?.charge_overtime_multiplier,
    payout_max_daily_regular_hours: settingsObj?.payout_max_daily_regular_hours,
    payout_max_daily_ot_hours: settingsObj?.payout_max_daily_overtime_hours,
    payout_overtime_multiplier: settingsObj?.payout_overtime_multiplier,
    charge_double_overtime_multiplier: settingsObj?.charge_double_overtime_multiplier,
    payout_double_overtime_multiplier: settingsObj?.payout_double_overtime_multiplier,
  };
};

export const getMarginPercent = (settings, settingsNestedOperator) => {
  const rateSettingHash = getRateSettingsHash(settings, settingsNestedOperator);
  const isW2 = settingsNestedOperator.includes('w2');

  if (isW2) {
    return settingsNestedOperator.includes('travel')
      ? rateSettingHash.margin_percent_w2_travel
      : rateSettingHash.total_margin_percent_w2;
  }

  return rateSettingHash.margin_percent_1099;
};

export const calculateTotalPay = (
  settings,
  settingsNestedOperator,
  hours,
  settingsType = 'non_blended_settings',
  chargeRateCents = null,
) => {
  const { chargeRateCents: defaultChargeRate } = getRateSettingsInfo(settings);
  const totalPayParams = createCalcTotalPayParams(settings, settingsNestedOperator, settingsType);
  const resolvedChargeRate = chargeRateCents ?? defaultChargeRate;
  if (!resolvedChargeRate) {
    return null;
  }

  return calcTotalPay({
    chargeRate: resolvedChargeRate,
    margin_percent: getMarginPercent(settings, settingsNestedOperator),
    hours,
    ...totalPayParams,
  });
};

export const createRateSettingField = (
  label,
  property,
  settingsNestedOperator,
  formatter = DisplayHelpers.roundToTwoDecimals,
) => {
  return (
    <FunctionField
      label={label}
      render={({ settings }) => {
        const { rateSettingsPrefix } = getRateSettingsInfo(settings, settingsNestedOperator);
        const rateSettingHash = getRateSettingsHash(settings, settingsNestedOperator);

        const value = rateSettingHash?.[rateSettingsPrefix]?.[property];
        return formatter(value);
      }}
    />
  );
};

export const getPayoutRate = (settings, settingsNestedOperator, defaultChargeRateCents = null) => {
  const marginPercent = getMarginPercent(settings, settingsNestedOperator);
  const rateSettingHash = getRateSettingsHash(settings, settingsNestedOperator);

  const {
    charge_max_daily_regular_hours,
    charge_max_daily_overtime_hours,
    charge_overtime_multiplier,
    payout_max_daily_regular_hours,
    payout_max_daily_overtime_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
    charge_double_overtime_multiplier,
  } = rateSettingHash[rateSettingHash.use_blended_rate ? 'blended_rates' : 'non_blended_rates'];

  const { chargeRateCents } = getRateSettingsInfo(settings, settingsNestedOperator);

  const payoutRateObj = {
    chargeRate: defaultChargeRateCents || chargeRateCents,
    margin_percent: marginPercent,
    charge_max_daily_regular_hours,
    charge_max_daily_ot_hours: charge_max_daily_overtime_hours,
    charge_overtime_multiplier,
    payout_max_daily_regular_hours,
    payout_max_daily_ot_hours: payout_max_daily_overtime_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
    charge_double_overtime_multiplier,
    hours: 12, // Assuming a default of 12 hours for calculation
  };

  const { payoutRate } = calcPayoutRate(payoutRateObj);

  return payoutRate;
};

export const getMarginSource = (settingsNestedOperator: string) => {
  const isW2 = settingsNestedOperator.includes('w2');
  if (isW2) {
    return settingsNestedOperator.includes('travel')
      ? 'settings.w2.travel.margin_percent_w2_travel'
      : 'settings.w2.local.total_margin_percent_w2';
  }

  return 'settings.ten99.margin_percent_1099';
};
